/**
 * layoutInvariant.ts —— 布局期不变量（阶段1 / 方案 B · 设计方案 §3.3）
 *
 * 与 `seatLayout.validateRing`（只护 pod）同源同风格，把 `inter()` 升级为 **OBB/SAT 相交**，
 * 覆盖 pod + 全部卫星两两不叠、不侵中心带/英雄区、不越界。硬约束：任意两非白名单元素 OBB
 * 相交面积 = 0（EPS 容差内）；白名单（同簇 pod↔自身 badge/牌背贴靠、heroCat↔英雄手牌）阈值
 * 按设计定稿常量。返回 `{ok, violations[]}`，violation 精确到对，便于 QA 定位。
 *
 * 纯几何、无 DOM、可毫秒级单测；进 vitest + CI red-line。
 */

import type { ElementKind, LayoutElement, LayoutSnapshot, OBB } from './overlayLayout';

const EPS = 0.01;

/**
 * Round21-M0 新增的 6 类关键区（A~F，设计 §9.1 R1）。
 *
 * **这些 kind 一律不参与 `assertNoOverlap`（旧口径布局期红线）**：M0 的目标是「补度量、不回退旧红线」。
 * 6 类关键区由独立的 `assertProtectedRegions` 按各自保护强度判定，避免把「真实遮挡」混进旧不变量
 * 导致既有 307 用例/运行时 `validateClusters` 无差别变红（会阻断 M0 合入，也污染 solver 回退判定）。
 */
export const PROTECTED_REGION_KINDS: ReadonlySet<ElementKind> = new Set<ElementKind>([
  'boardSlot',
  'heroCard',
  'heroCat',
  'seatMade',
  'seatBadges',
  'panel',
]);

const isProtectedRegionKind = (k: ElementKind): boolean => PROTECTED_REGION_KINDS.has(k);

// ---------------------------------------------------------------------------
// SAT 几何工具（OBB 相交 / 相交面积）
// ---------------------------------------------------------------------------

interface Vec {
  x: number;
  y: number;
}

/** OBB 的本地两条单位轴（经 rot 旋转；顺时针为正，与层坐标 y 向下一致）。 */
function axesOf(o: OBB): [Vec, Vec] {
  const c = Math.cos(o.rot);
  const s = Math.sin(o.rot);
  return [
    { x: c, y: s }, // 本地 x 轴
    { x: -s, y: c }, // 本地 y 轴
  ];
}

/** OBB 沿单位向量 n 的支撑半径。 */
function projRadius(o: OBB, n: Vec): number {
  const [ax, ay] = axesOf(o);
  return o.hw * Math.abs(ax.x * n.x + ax.y * n.y) + o.hh * Math.abs(ay.x * n.x + ay.y * n.y);
}

/** OBB 四顶点（层坐标，含旋转）。 */
export function corners(o: OBB): Vec[] {
  const [ax, ay] = axesOf(o);
  const out: Vec[] = [];
  for (const sx of [-1, 1]) {
    for (const sy of [-1, 1]) {
      out.push({
        x: o.cx + sx * o.hw * ax.x + sy * o.hh * ay.x,
        y: o.cy + sx * o.hw * ax.y + sy * o.hh * ay.y,
      });
    }
  }
  // 排成凸多边形顺序（[-,-],[-,+],[+,+],[+,-]）。
  return [out[0], out[1], out[3], out[2]];
}

/**
 * SAT 分离判定 + 最小穿透深度（MTV）。
 * `overlap=false` 表示存在分离轴（相交面积 = 0）；`depth` 为最小穿透深度（overlap 时 > 0）。
 */
export function obbSeparation(a: OBB, b: OBB): { overlap: boolean; depth: number } {
  const axes = [...axesOf(a), ...axesOf(b)];
  let minDepth = Infinity;
  const ca: Vec = { x: a.cx, y: a.cy };
  const cb: Vec = { x: b.cx, y: b.cy };
  for (const ax of axes) {
    const da = ca.x * ax.x + ca.y * ax.y;
    const db = cb.x * ax.x + cb.y * ax.y;
    const ra = projRadius(a, ax);
    const rb = projRadius(b, ax);
    const gap = Math.abs(da - db) - (ra + rb);
    if (gap > EPS) return { overlap: false, depth: 0 };
    minDepth = Math.min(minDepth, -gap);
  }
  return { overlap: true, depth: Math.max(0, minDepth) };
}

/** OBB 是否相交（相交面积 > 0）。 */
export function obbOverlap(a: OBB, b: OBB): boolean {
  return obbSeparation(a, b).overlap;
}

function polygonArea(poly: Vec[]): number {
  let s = 0;
  for (let i = 0; i < poly.length; i++) {
    const p = poly[i];
    const q = poly[(i + 1) % poly.length];
    s += p.x * q.y - q.x * p.y;
  }
  return Math.abs(s) / 2;
}

/** 保证多边形为逆时针（标准数学正向：signed area > 0）。 */
function ensureCCW(poly: Vec[]): Vec[] {
  let s = 0;
  for (let i = 0; i < poly.length; i++) {
    const p = poly[i];
    const q = poly[(i + 1) % poly.length];
    s += p.x * q.y - q.x * p.y;
  }
  return s < 0 ? [...poly].reverse() : poly;
}

/** Sutherland–Hodgman 凸多边形裁剪：用 clip（凸、CCW）裁 subject，返回交多边形。 */
function clipPolygon(subject: Vec[], clip: Vec[]): Vec[] {
  let output = subject;
  const n = clip.length;
  for (let i = 0; i < n; i++) {
    const a = clip[i];
    const b = clip[(i + 1) % n];
    const edgeX = b.x - a.x;
    const edgeY = b.y - a.y;
    // CCW 多边形：内侧 = 边向量左手侧，cross(edge, p-a) >= 0。
    const inside = (p: Vec) => edgeX * (p.y - a.y) - edgeY * (p.x - a.x) >= -EPS;
    const input = output;
    output = [];
    if (input.length === 0) break;
    for (let j = 0; j < input.length; j++) {
      const cur = input[j];
      const prev = input[(j + input.length - 1) % input.length];
      const curIn = inside(cur);
      const prevIn = inside(prev);
      if (curIn) {
        if (!prevIn) {
          const ip = intersectSeg(prev, cur, a, b);
          if (ip) output.push(ip);
        }
        output.push(cur);
      } else if (prevIn) {
        const ip = intersectSeg(prev, cur, a, b);
        if (ip) output.push(ip);
      }
    }
  }
  return output;
}

function intersectSeg(p1: Vec, p2: Vec, a: Vec, b: Vec): Vec | null {
  const r = { x: p2.x - p1.x, y: p2.y - p1.y };
  const s = { x: b.x - a.x, y: b.y - a.y };
  const denom = r.x * s.y - r.y * s.x;
  if (Math.abs(denom) < 1e-12) return null;
  const t = ((a.x - p1.x) * s.y - (a.y - p1.y) * s.x) / denom;
  return { x: p1.x + t * r.x, y: p1.y + t * r.y };
}

/** 两 OBB 的相交面积（凸多边形裁剪 + 鞋带面积）。 */
export function obbIntersectArea(a: OBB, b: OBB): number {
  if (!obbOverlap(a, b)) return 0;
  const polyA = ensureCCW(corners(a));
  const polyB = ensureCCW(corners(b));
  const clip = clipPolygon(polyA, polyB);
  if (clip.length < 3) return 0;
  return polygonArea(clip);
}

// ---------------------------------------------------------------------------
// 白名单 + 策略常量（设计 §3.3.2 定稿）
// ---------------------------------------------------------------------------

/** 白名单阈值常量（写入 policy，被单测断言）。 */
export const BADGE_OVERLAP_MAX = 0.18; // badge OBB 面积的 18%
export const CARDBACK_OVERLAP_MAX = 0.12; // cardback OBB 面积的 12%
export const PENETRATION_MAX_BADGE = 3; // px
export const PENETRATION_MAX_CARDBACK = 2; // px

export interface WhitelistPair {
  a: string;
  b: string;
}

/**
 * pod 文本子矩形（`podTextRect`）相对 pod 盒（左上角原点）的比例（设计 §3.3.2）。
 *
 * 「昵称行（`.seat__row1`）+ 筹码行（`.seat__row2`）」的并集子矩形 —— pod 内缩、位于中下部：
 * 上缘 0.35h 之上是头像/SB·BB·D 徽标带（`.seat__row1` 头像 + `.seat__badges` 角标），
 * 属**允许装饰性贴靠**区；0.35h~0.9h 才是**不可被压**的昵称/筹码/状态文本。左右各内缩 0.1w
 * 让开 pod 圆角与边缘描边。设计 §3.3.2 给的 full/compact 口径 `[0.1w,0.35h]~[0.9w,0.9h]`；
 * row/micro 档 pod 更矮、文本占比更高，用同一保守比例建模（等价于把红线区取到「文本必然覆盖」
 * 的最小并集），不会漏保护（宁可更严）。此为「整 pod 面积比 ≤18%/12% 近似」升级为的**精确红线**。
 */
export const POD_TEXT_RECT = {
  xMin: 0.1,
  xMax: 0.9,
  yMin: 0.35,
  yMax: 0.9,
} as const;

/**
 * 由 pod OBB 推导其 `podTextRect` 子矩形 OBB（设计 §3.3.2 文本保护区）。
 * pod 恒为轴对齐（rot=0，见 overlayLayout 各构造点），故直接按相对比例在 pod 局部盒取中下部子矩形。
 */
export function podTextRect(pod: OBB): OBB {
  const w = 2 * pod.hw;
  const h = 2 * pod.hh;
  const cxRel = (POD_TEXT_RECT.xMin + POD_TEXT_RECT.xMax) / 2;
  const cyRel = (POD_TEXT_RECT.yMin + POD_TEXT_RECT.yMax) / 2;
  const hwRel = (POD_TEXT_RECT.xMax - POD_TEXT_RECT.xMin) / 2;
  const hhRel = (POD_TEXT_RECT.yMax - POD_TEXT_RECT.yMin) / 2;
  // pod 左上角 = (cx - hw, cy - hh)；子矩形中心 = 左上角 + (cxRel·w, cyRel·h)。
  return {
    cx: pod.cx - pod.hw + cxRel * w,
    cy: pod.cy - pod.hh + cyRel * h,
    hw: hwRel * w,
    hh: hhRel * h,
    rot: 0,
  };
}

/** 会被 `podTextRect` 红线保护的「卫星」种类（任何卫星都不得压文本子矩形，设计 §3.3.2）。 */
const SATELLITE_KINDS = new Set<string>(['cardback', 'chip', 'pill', 'badge']);

export interface LayoutPolicy {
  /** 面积/穿透容差。 */
  eps: number;
  /** 卫星到中心带最小间隙。 */
  centerBandGap: number;
  /** badge 白名单：相交面积上限占比 + 穿透上限。 */
  badgeOverlapMax: number;
  penetrationMaxBadge: number;
  /** cardback 白名单。 */
  cardbackOverlapMax: number;
  penetrationMaxCardback: number;
}

export const LAYOUT_POLICY_DEFAULT: LayoutPolicy = {
  eps: EPS,
  centerBandGap: 4,
  badgeOverlapMax: BADGE_OVERLAP_MAX,
  penetrationMaxBadge: PENETRATION_MAX_BADGE,
  cardbackOverlapMax: CARDBACK_OVERLAP_MAX,
  penetrationMaxCardback: PENETRATION_MAX_CARDBACK,
};

const CENTER_KINDS = new Set(['centerBand', 'board', 'pot']);

/**
 * 白名单（闭集，设计 §3.3.2）。返回豁免阈值；返回 null = 不豁免（走硬约束 = 0）。
 * `refKind` 指明面积占比基于哪个元素的盒（'a' = 第一个，'b' = 第二个）。
 */
export function isWhitelisted(
  a: LayoutElement,
  b: LayoutElement,
  policy: LayoutPolicy = LAYOUT_POLICY_DEFAULT,
): { maxAreaFrac: number; maxPenetration: number; refKind: 'a' | 'b' } | null {
  // 中心簇内部（centerBand/board/pot 两两）：受保护区自身构造，豁免。
  if (CENTER_KINDS.has(a.kind) && CENTER_KINDS.has(b.kind)) {
    return { maxAreaFrac: Infinity, maxPenetration: Infinity, refKind: 'a' };
  }
  // 同簇（同席位）豁免。
  if (a.seat !== null && a.seat === b.seat) {
    const kinds = new Set([a.kind, b.kind]);
    // 同簇 pod ↔ 自身 inPod 承载卫星（R21-M2 §4.1 P3 动作条 / P4 牌背小徽 / P2 筹码）：
    // 卫星置于 pod 顶部标识带内，允许与自身 pod 相交（`degraded==='inPod'` 显式标记，仅此路径豁免）。
    // 注意：**不豁免 podTextRect**——中下部昵称/筹码文本子矩形仍由 assertNoOverlap 1b 单独护红线，
    // inPod 卫星必须落在 pod 上缘 0.35h 标识带、绝不碰文本区（落位时构造保证）。
    const inPodSat = (x: LayoutElement) => x.degraded === 'inPod' && (x.kind === 'chip' || x.kind === 'pill' || x.kind === 'cardback');
    if (kinds.has('pod') && (inPodSat(a) || inPodSat(b))) {
      return { maxAreaFrac: Infinity, maxPenetration: Infinity, refKind: 'a' };
    }
    // Round25 · M2.1（round25-pill-must-visible）：同一席位自身的 inPod 卫星之间（pill/cardback/chip
    // 均落在自己 pod 的上缘标识带里）允许受控共位。行动 pill 是**硬可见约束**，穷尽常规解仍放不下时
    // 强制 inPod（见 seatLayout.commit 的 forced 分支）；此时它与本座自身 inPod 牌背/筹码在同一带内
    // 近邻属正常构造（非跨座侵犯），标称盒重叠豁免——真实可见性由 real-DOM 验收（A5/A6/A7）判红线。
    if (inPodSat(a) && inPodSat(b)) {
      return { maxAreaFrac: Infinity, maxPenetration: Infinity, refKind: 'a' };
    }
    // 同簇 pod ↔ 自身 badge。
    if (kinds.has('pod') && kinds.has('badge')) {
      return {
        maxAreaFrac: policy.badgeOverlapMax,
        maxPenetration: policy.penetrationMaxBadge,
        refKind: a.kind === 'badge' ? 'a' : 'b',
      };
    }
    // 同簇 pod ↔ 自身 cardback。
    if (kinds.has('pod') && kinds.has('cardback')) {
      return {
        maxAreaFrac: policy.cardbackOverlapMax,
        maxPenetration: policy.penetrationMaxCardback,
        refKind: a.kind === 'cardback' ? 'a' : 'b',
      };
    }
    // 英雄簇：`heroExclusion / pod` 互相受控贴靠（英雄 pod 与其排他矩形，展示层）。
    // R21-R3 白名单收窄：**删除** `heroCat / cardback` 的 heroFree 豁免——`heroCat` 升为 C 类关键区，
    // 与英雄手牌改判「相邻不相交」（由 assertProtectedRegions 管，间隙 ≥6），不再在此互相豁免。
    const heroFree = new Set(['heroExclusion', 'pod']);
    if (heroFree.has(a.kind) && heroFree.has(b.kind)) {
      return { maxAreaFrac: Infinity, maxPenetration: Infinity, refKind: 'a' };
    }
  }
  return null;
}

function area4(o: OBB): number {
  return 4 * o.hw * o.hh;
}

// ---------------------------------------------------------------------------
// 不变量
// ---------------------------------------------------------------------------

export interface NoOverlapResult {
  ok: boolean;
  violations: string[];
}

/**
 * 布局期不变量（设计 §3.3）：
 * - 硬约束：任意两元素 OBB 相交面积 ≤ eps，除非命中白名单；白名单对相交面积/穿透 ≤ 阈值。
 * - 区域约束：每卫星 vs centerBand 分离 ≥ centerBandGap；不越内容盒边界。
 */
export function assertNoOverlap(s: LayoutSnapshot, policy: LayoutPolicy = LAYOUT_POLICY_DEFAULT): NoOverlapResult {
  const v: string[] = [];
  const els = s.elements;
  const box = s.box;

  // 1. 两两相交。
  for (let i = 0; i < els.length; i++) {
    for (let j = i + 1; j < els.length; j++) {
      const A = els[i];
      const B = els[j];
      // Round21-M0：6 类关键区不参与旧口径红线（由 assertProtectedRegions 判定），跳过。
      if (isProtectedRegionKind(A.kind) || isProtectedRegionKind(B.kind)) continue;
      // 中心簇内部跳过（受保护区自身构造）。
      if (CENTER_KINDS.has(A.kind) && CENTER_KINDS.has(B.kind)) continue;
      const sep = obbSeparation(A.obb, B.obb);
      if (!sep.overlap) continue;
      const area = obbIntersectArea(A.obb, B.obb);
      const wl = isWhitelisted(A, B, policy);
      if (wl) {
        const refArea = wl.refKind === 'a' ? area4(A.obb) : area4(B.obb);
        const maxArea = wl.maxAreaFrac === Infinity ? Infinity : wl.maxAreaFrac * refArea;
        if (area > maxArea + policy.eps) {
          v.push(`#${A.id} ↔ #${B.id} 白名单超额 area=${area.toFixed(1)} > ${maxArea.toFixed(1)}`);
        } else if (sep.depth > wl.maxPenetration + policy.eps) {
          v.push(`#${A.id} ↔ #${B.id} 白名单穿透 depth=${sep.depth.toFixed(1)} > ${wl.maxPenetration}`);
        }
      } else if (area > policy.eps) {
        v.push(`#${A.id} ↔ #${B.id} 重叠 area=${area.toFixed(1)}`);
      }
    }
  }

  // 1b. podTextRect 精确红线（设计 §3.3.2）：任何卫星（cardback/chip/pill/badge）与任一 pod 的
  //     「昵称行 + 筹码行」文本子矩形相交面积必须 = 0（穿透 0）。此约束与上面的「整 pod OBB 面积比
  //     ≤18%/12% + 浅穿透」白名单**并存**：外缘装饰性贴靠仍允许（pod 角/上缘头像·徽标带），但绝不许
  //     碰到中下部文本区。把间接近似升级为精确对齐设计红线，防未来 pod 内布局调整时文本保护失效不被拦。
  const podEls = els.filter((e) => e.kind === 'pod');
  if (podEls.length > 0) {
    for (const sat of els) {
      if (!SATELLITE_KINDS.has(sat.kind)) continue;
      for (const pod of podEls) {
        const tr = podTextRect(pod.obb);
        if (!obbOverlap(sat.obb, tr)) continue;
        const area = obbIntersectArea(sat.obb, tr);
        if (area > policy.eps) {
          v.push(`#${sat.id} 压 #${pod.id}.podTextRect area=${area.toFixed(1)}（禁止压昵称/筹码文本子矩形）`);
        }
      }
    }
  }

  // 2. 卫星 vs 中心带间隙。
  const bandEl = els.find((e) => e.kind === 'centerBand');
  if (bandEl) {
    const grown: OBB = { ...bandEl.obb, hw: bandEl.obb.hw + policy.centerBandGap, hh: bandEl.obb.hh + policy.centerBandGap };
    for (const e of els) {
      if (isProtectedRegionKind(e.kind)) continue;
      if (e.kind === 'centerBand' || e.kind === 'board' || e.kind === 'pot' || e.kind === 'pod' || e.kind === 'heroExclusion') continue;
      if (obbOverlap(e.obb, grown)) {
        v.push(`#${e.id} 侵入中心带间隙(<${policy.centerBandGap})`);
      }
    }
  }

  // 3. 越界（用旋转后 AABB）。
  for (const e of els) {
    if (isProtectedRegionKind(e.kind)) continue;
    if (e.kind === 'centerBand' || e.kind === 'heroExclusion') continue;
    const c = Math.abs(Math.cos(e.obb.rot));
    const sn = Math.abs(Math.sin(e.obb.rot));
    const hw = e.obb.hw * c + e.obb.hh * sn;
    const hh = e.obb.hw * sn + e.obb.hh * c;
    if (e.obb.cx - hw < -EPS || e.obb.cy - hh < -EPS || e.obb.cx + hw > box.w + EPS || e.obb.cy + hh > box.h + EPS) {
      v.push(`#${e.id} 越出内容盒`);
    }
  }

  return { ok: v.length === 0, violations: v };
}

// ---------------------------------------------------------------------------
// Round21-M0 · 关键区保护集不变量 assertProtectedRegions（设计 §9.1 R11 / PRD AC-B1/AC-B2）
// ---------------------------------------------------------------------------

/** 一组 OBB 的呼吸带膨胀（各边 +by，rot 保持）。 */
function growObbBy(o: OBB, by: number): OBB {
  return { ...o, hw: o.hw + by, hh: o.hh + by };
}

/**
 * 关键区保护策略（设计 §4.4 各区几何 + 保护强度）。所有阈值写入 policy 常量、被单测断言。
 */
export interface ProtectionPolicy {
  eps: number;
  /** A 类：公共牌槽呼吸带（slot ⊕ boardSlotGap 内不得进入任何异类元素），设计 §4.4.3 「slot ⊕ 8」。 */
  boardSlotGap: number;
  /** B 类：英雄手牌保护呼吸带（cards ⊕ heroCardGap），设计 §4.4.4 「两张并集 ⊕ 6」。 */
  heroCardGap: number;
  /** C 类：hero.cat 保护带（与英雄手牌「相邻不相交」间隙 ≥ heroCatGap），设计 R3。 */
  heroCatGap: number;
}

export const PROTECTION_POLICY_DEFAULT: ProtectionPolicy = {
  eps: EPS,
  boardSlotGap: 8,
  heroCardGap: 6,
  heroCatGap: 6,
};

export interface ProtectedViolation {
  /** 关键区元素 id。 */
  region: string;
  regionKind: ElementKind;
  /** 遮挡/侵入源元素 id。 */
  occluder: string;
  occluderKind: ElementKind;
  /** 与关键区 OBB 本体（不含呼吸带）的相交面积 px²（对齐 QA 报告数值口径）。 */
  area: number;
  /** 关键区可见比（1.000 = 完全可见；由累计遮挡面积裁剪，clamp[0,1]）。 */
  visFrac: number;
}

export interface ProtectedRegionResult {
  ok: boolean;
  violations: ProtectedViolation[];
}

/** 板牌自身构造族（互不构成遮挡）。 */
const BOARD_FAMILY = new Set<ElementKind>(['board', 'boardSlot', 'centerBand', 'pot']);

/** 每个受保护关键区的判定口径。 */
interface RegionSpec {
  /** 呼吸带（对 A/B/C 生效；D/E 取 0）。 */
  gap: number;
  /**
   * 是否 z 感知遮挡（设计 N-6 静态偏序）：true → 仅「更高 z 平面」的异类元素才算遮挡（D/E）；
   * false → 任意异类元素进入呼吸带即算侵入（A/B/C 几何硬零，与绘序无关）。
   */
  zAware: boolean;
}

const REGION_SPEC: Record<string, RegionSpec> = {
  boardSlot: { gap: 0, zAware: false }, // gap 由 policy.boardSlotGap 覆盖
  heroCard: { gap: 0, zAware: false }, // gap 由 policy.heroCardGap 覆盖
  heroCat: { gap: 0, zAware: false }, // gap 由 policy.heroCatGap 覆盖
  seatMade: { gap: 0, zAware: true },
  seatBadges: { gap: 0, zAware: true },
};

/**
 * 判断 `occ`（候选遮挡源）对关键区 `region` 是否属**允许贴靠**（闭集白名单，AC-B7：关键区 A~F 不豁免跨类遮挡）。
 * 返回 true = 允许（不计违例）；false = 需判定遮挡。
 */
function protectionWhitelisted(region: LayoutElement, occ: LayoutElement): boolean {
  if (region.id === occ.id) return true;
  // 板牌自身构造族互不遮挡（centerBand 就是槽并集包络）。
  if (region.kind === 'boardSlot' && BOARD_FAMILY.has(occ.kind)) return true;
  // 英雄手牌 / 牌型提示：英雄自身容器（英雄 pod / 排他区）与手牌互相之间允许贴靠。
  if (region.kind === 'heroCard' || region.kind === 'heroCat') {
    if (occ.kind === 'heroExclusion') return true;
    if (occ.kind === 'pod' && occ.seat === region.seat) return true; // 英雄自身 pod
    if (region.kind === 'heroCard' && occ.kind === 'heroCard') return true; // 两张手牌相邻
  }
  // 摊牌牌型 / 席位徽标：允许挂在自身 pod（同席 pod 是其容器）。
  if ((region.kind === 'seatMade' || region.kind === 'seatBadges') && occ.kind === 'pod' && occ.seat === region.seat) {
    return true;
  }
  return false;
}

/**
 * 关键区保护集不变量（设计 §9.1 R11 · 设计 §11）：对 6 类关键区（A 真实公共牌槽 / B 英雄手牌 /
 * C hero.cat / D `.seat__made` / E `.seat__badges`；F `panel` 作为遮挡源参与判定）按各自保护强度
 * （硬零相交 vs z 感知遮挡）判定，逐关键区给出 `visFrac` 与侵入/遮挡面积。
 *
 * ## 保护强度（设计 §4.4）
 * - **A/B/C（几何硬零）**：任何异类元素进入「关键区 ⊕ 呼吸带」即违例，**与绘序无关**（对手牌背压真实
 *   牌槽 279px²、浮层压牌槽 1056px²、气泡压手牌 143.4px²、浮层压 hero.cat 均在此被检出）。
 * - **D/E（z 感知遮挡，设计 N-6）**：仅「更高 z 平面」的异类元素算遮挡（同席徽标 z=5 压 `.seat__made`
 *   z=1 的 644px² 在此被检出）。
 *
 * `visFrac = 1 − 累计遮挡面积 / 关键区面积`（clamp[0,1]）。任一关键区 `visFrac < 1` 或存在侵入 → 违例。
 *
 * **M0 目标是「如实暴露」这些真实遮挡**（此前它们从未进入度量），不负责把它们修成 0（那是 M1/M2）。
 */
export function assertProtectedRegions(
  s: LayoutSnapshot,
  policy: ProtectionPolicy = PROTECTION_POLICY_DEFAULT,
): ProtectedRegionResult {
  const violations: ProtectedViolation[] = [];
  const els = s.elements;

  for (const region of els) {
    const spec = REGION_SPEC[region.kind];
    if (!spec) continue; // 仅 A/B/C/D/E 五类是「被保护」的关键区（panel 只作遮挡源）
    const gap =
      region.kind === 'boardSlot'
        ? policy.boardSlotGap
        : region.kind === 'heroCard'
          ? policy.heroCardGap
          : region.kind === 'heroCat'
            ? policy.heroCatGap
            : spec.gap;
    const guard = gap > 0 ? growObbBy(region.obb, gap) : region.obb;
    const regionArea = 4 * region.obb.hw * region.obb.hh;
    let occludedBody = 0;

    for (const occ of els) {
      if (occ === region) continue;
      if (protectionWhitelisted(region, occ)) continue;
      // z 感知区：仅更高绘序平面算遮挡；几何硬零区：任意平面。
      if (spec.zAware && occ.z <= region.z) continue;
      // 侵入呼吸带才算（A/B/C），或直接与本体相交（D/E，gap=0 → guard===region.obb）。
      if (!obbOverlap(occ.obb, guard)) continue;
      const bodyArea = obbIntersectArea(occ.obb, region.obb);
      const bandArea = gap > 0 ? obbIntersectArea(occ.obb, guard) : bodyArea;
      if (bandArea <= policy.eps) continue;
      occludedBody += bodyArea;
      violations.push({
        region: region.id,
        regionKind: region.kind,
        occluder: occ.id,
        occluderKind: occ.kind,
        area: round1(bodyArea),
        visFrac: 0, // 逐源占位，下方按关键区聚合后回填
      });
    }

    if (occludedBody > policy.eps) {
      const visFrac = Math.max(0, 1 - occludedBody / (regionArea || 1));
      // 回填本关键区所有违例的 visFrac（聚合口径，设计 §11 visFrac===1.000）。
      for (const vio of violations) {
        if (vio.region === region.id && vio.visFrac === 0) vio.visFrac = round3(visFrac);
      }
    }
  }

  return { ok: violations.length === 0, violations };
}

function round1(v: number): number {
  return Math.round(v * 10) / 10;
}
function round3(v: number): number {
  return Math.round(v * 1000) / 1000;
}

// ---------------------------------------------------------------------------
// Round21-M0 · 归属性不变量 assertAttribution（门禁骨架 · 桩函数占位，真正实现放 R21-M2）
// ---------------------------------------------------------------------------

/**
 * 归属性策略阈值（设计 §9.1 R4 定稿）。M0 仅定义接口/常量占位，`assertAttribution` 不做实际判定。
 */
export interface AttributionPolicy {
  /** 归属度硬上界。 */
  kappaHard: number;
  /** 逐承载模式设计目标。 */
  kappaDesign: { radial: number; tangential: number; inPod: number };
  /** 主人内缘间隙上界（pill/chip/cardback : badge : 切向）。 */
  edgeGapMax: { own: number; badge: number; tangential: number };
  /** 跨主人外缘最小间距。 */
  sMinCrossOwner: number;
  /** 跨主人中心距最小值。 */
  centerDistMin: number;
  /** 度量数值容差（浮点/取整噪声，避免边界抖动误判）——仅用于**距离/像素**量纲。 */
  eps: number;
  /** 归属比 κ 的容差（无量纲比值，独立于像素 eps；κ 已取整到 3 位小数，此处仅吸收取整噪声）。 */
  kappaEps: number;
}

export const ATTRIBUTION_POLICY_DEFAULT: AttributionPolicy = {
  kappaHard: 0.6,
  kappaDesign: { radial: 0.55, tangential: 0.5, inPod: 0.35 },
  edgeGapMax: { own: 12, badge: 8, tangential: 6 },
  sMinCrossOwner: 12,
  centerDistMin: 24,
  eps: 0.5,
  kappaEps: 0.005,
};

export interface AttributionResult {
  ok: boolean;
  violations: string[];
  /**
   * 逐卫星归属度量（诊断/报告用）：kappa、edgeGapOwn、d_own、d_nearest_other、承载模式。
   * R21-M2 起为**真实判定**，不再是桩函数。
   */
  measures: AttributionMeasureRow[];
}

export interface AttributionMeasureRow {
  id: string;
  kind: ElementKind;
  seat: number | null;
  edgeGapOwn: number;
  dOwn: number;
  dNearestOther: number;
  kappa: number;
  carrier: 'radial' | 'tangential' | 'inPod' | 'unknown';
}

/** 参与归属判定的卫星种类（设计 §4.1：pill/chip/badge/cardback）。 */
const ATTRIBUTION_SATELLITE_KINDS = new Set<ElementKind>(['chip', 'pill', 'badge', 'cardback']);

/** 跨主人间距判定涉及的卫星种类（AC-A3：pill/chip/badge）。 */
const CROSS_OWNER_KINDS = new Set<ElementKind>(['chip', 'pill', 'badge']);

/** OBB 中心距。 */
function centerDist(a: OBB, b: OBB): number {
  return Math.hypot(a.cx - b.cx, a.cy - b.cy);
}

/**
 * 归属性不变量（设计 §11 · PRD AC-A1~A8）——**R21-M2 真实判定**。
 *
 * 对每个卫星（pill/chip/badge/cardback）断言：
 * - **贴靠上限**：到主人 pod 外缘间隙 `edgeGapOwn ≤ G_max`（badge ≤ 8px，其余 ≤ 12px，inPod = 0）；
 * - **唯一归属判别式**：`d_own ≤ κ_hard · d_nearest_other`（即 `d_own/d_nearest_other ≤ 0.6`，p100，0 例外）；
 * - **跨主人最小语义间距**（AC-A3）：不同主人的 pill/chip/badge 两两**中心距 ≥ centerDistMin(24)**、
 *   **外缘间距 ≥ sMinCrossOwner(12)**；
 * - **已淘汰玩家 pod 不参与**（AC-A8）：既不作为主人，也不作为 `d_nearest_other` 候选。
 *
 * 承载模式（radial/tangential/inPod）由构造保证 κ ≤ 0.55/0.50/0.35，本函数按**实测几何**判定，
 * 不读取任何「设计声明值」——快照若 κ 超标即判红（CI 阻断），**不存在放宽 κ 的代码路径**。
 */
export function assertAttribution(
  s: LayoutSnapshot,
  policy: AttributionPolicy = ATTRIBUTION_POLICY_DEFAULT,
): AttributionResult {
  const v: string[] = [];
  const measures: AttributionMeasureRow[] = [];
  const els = s.elements;
  // 在局 pod（排除已淘汰）。
  const pods = els.filter((e) => e.kind === 'pod' && !e.eliminated);
  const podBySeat = new Map<number, LayoutElement>();
  for (const p of pods) if (p.seat !== null) podBySeat.set(p.seat, p);

  const satellites = els.filter((e) => ATTRIBUTION_SATELLITE_KINDS.has(e.kind) && e.seat !== null);

  for (const sat of satellites) {
    const owner = podBySeat.get(sat.seat!);
    if (!owner) {
      // 主人 pod 缺失（或已淘汰）——归属无从判定，视为违例（AC-A8：淘汰席不应再有卫星）。
      v.push(`#${sat.id} 无在局主人 pod（seat=${sat.seat}）`);
      continue;
    }
    const others = pods.filter((p) => p.seat !== sat.seat);
    const dOwn = centerDist(sat.obb, owner.obb);
    const sepOwn = obbSeparation(sat.obb, owner.obb);
    // edgeGapOwn：与主人 pod 相交（inPod/贴靠）→ 0；否则用 SAT 分离深度的相反数近似外缘净空。
    let edgeGapOwn = 0;
    if (!sepOwn.overlap) {
      // 沿卫星→pod 中心方向的外缘净空 = 中心距 − 两盒在该方向的支撑半径。
      const dir = { x: (owner.obb.cx - sat.obb.cx) / (dOwn || 1), y: (owner.obb.cy - sat.obb.cy) / (dOwn || 1) };
      const supOwner = projRadius(owner.obb, dir);
      const supSat = projRadius(sat.obb, dir);
      edgeGapOwn = Math.max(0, dOwn - supOwner - supSat);
    }
    let dNearestOther = Number.POSITIVE_INFINITY;
    for (const o of others) {
      const d = centerDist(sat.obb, o.obb);
      if (d < dNearestOther) dNearestOther = d;
    }
    const kappa = Number.isFinite(dNearestOther) && dNearestOther > 0 ? dOwn / dNearestOther : 0;
    const carrier: AttributionMeasureRow['carrier'] =
      sepOwn.overlap || sat.degraded === 'inPod' ? 'inPod' : sat.degraded === 'tangential' ? 'tangential' : 'radial';
    measures.push({ id: sat.id, kind: sat.kind, seat: sat.seat, edgeGapOwn: round1(edgeGapOwn), dOwn: round1(dOwn), dNearestOther: round1(dNearestOther), kappa: round3(kappa), carrier });

    const gapMax = sat.kind === 'badge' ? policy.edgeGapMax.badge : policy.edgeGapMax.own;
    if (edgeGapOwn > gapMax + policy.eps) {
      v.push(`#${sat.id} edgeGapOwn=${edgeGapOwn.toFixed(1)} > ${gapMax}（贴靠上限）`);
    }
    if (Number.isFinite(dNearestOther) && kappa > policy.kappaHard + policy.kappaEps) {
      v.push(`#${sat.id} κ=d_own/d_other=${kappa.toFixed(3)} > ${policy.kappaHard}（归属歧义）`);
    }
  }

  // 跨主人最小语义间距（AC-A3）：不同主人的 pill/chip/badge 两两中心距 ≥24、外缘间距 ≥12。
  const crossSats = satellites.filter((e) => CROSS_OWNER_KINDS.has(e.kind));
  for (let i = 0; i < crossSats.length; i++) {
    for (let j = i + 1; j < crossSats.length; j++) {
      const a = crossSats[i];
      const b = crossSats[j];
      if (a.seat === b.seat) continue; // 同主人成组，允许 ≤6px 贴靠
      const cd = centerDist(a.obb, b.obb);
      if (cd < policy.centerDistMin - policy.eps) {
        v.push(`#${a.id} ↔ #${b.id} 跨主人中心距 ${cd.toFixed(1)} < ${policy.centerDistMin}`);
        continue;
      }
      const sep = obbSeparation(a.obb, b.obb);
      // 外缘间距：分离时 = 中心距 − 两支撑半径（沿连线方向）；相交时 = 0。
      let edge = 0;
      if (!sep.overlap) {
        const dir = { x: (b.obb.cx - a.obb.cx) / (cd || 1), y: (b.obb.cy - a.obb.cy) / (cd || 1) };
        edge = Math.max(0, cd - projRadius(a.obb, dir) - projRadius(b.obb, dir));
      }
      if (edge < policy.sMinCrossOwner - policy.eps) {
        v.push(`#${a.id} ↔ #${b.id} 跨主人外缘间距 ${edge.toFixed(1)} < ${policy.sMinCrossOwner}`);
      }
    }
  }

  return { ok: v.length === 0, violations: v, measures };
}

// ---------------------------------------------------------------------------
// Round21-M7 · AC-D6 英雄区→操作条死区不变量 assertHeroActionDeadZone（BUG-04 校正口径）
// ---------------------------------------------------------------------------

/**
 * AC-D6 死区上限（英雄手牌/牌型提示/pod 底缘 → 操作条顶缘的纯 felt 空白）。
 */
export const DEAD_ZONE_MAX = 24;

/**
 * 死区判定策略。
 * - `edge`：竖屏环布英雄 pod 底沿相对毛毡内容盒下沿的内缩（planRing 的 `edge`，缺省 8）。
 * - `feltBorderBottom`：`.felt--stack-ring` 底边描边宽（毛毡边框盒下沿 → padding 盒下沿），
 *   与 `edge` 一起构成「页脚零抖动时」的设计死区基线（≈18px）。
 */
export interface DeadZonePolicy {
  eps: number;
  deadZoneMax: number;
  edge: number;
  feltBorderBottom: number;
}

export const DEAD_ZONE_POLICY_DEFAULT: DeadZonePolicy = {
  eps: 0.5,
  deadZoneMax: DEAD_ZONE_MAX,
  edge: 8,
  feltBorderBottom: 10,
};

/**
 * 操作条（页脚）分泳道模型 —— BUG-04 根因是页脚在「行动态 ↔ 稳态」间抖动，冻结的英雄 pod
 * 不跟随，抖动量原样变成死区。用泳道显式建模两态高度差：
 *
 * - 时限进度条泳道：`clockReservedH` 为**恒定预留**高（stack 恒 6），`clockNaturalH` 为计时态自然高。
 * - 信息条：`infoReservedH` 为 min-height 预留（stack 按 2 行 54 预留），
 *   `infoActingNaturalH` / `infoSteadyNaturalH` 为两态自然高（满档 2 行 / 收成 1 行）。
 *
 * 页脚某态高度 = max(clockReservedH, 该态时限条) + max(infoReservedH, 该态信息条) + 常量泳道。
 * 只要预留覆盖两态自然高，`actingFootH === steadyFootH`，页脚零抖动 → 死区 = 设计基线。
 */
export interface FootLaneModel {
  clockReservedH: number;
  clockNaturalH: number;
  infoReservedH: number;
  infoActingNaturalH: number;
  infoSteadyNaturalH: number;
}

/** 页脚在行动态 / 稳态下的可变泳道高度（不含随两态恒定的常量泳道，差值即抖动量）。 */
function footVariableH(m: FootLaneModel, state: 'acting' | 'steady'): number {
  const clock = state === 'acting' ? Math.max(m.clockReservedH, m.clockNaturalH) : m.clockReservedH;
  const info = Math.max(m.infoReservedH, state === 'acting' ? m.infoActingNaturalH : m.infoSteadyNaturalH);
  return clock + info;
}

export interface DeadZoneResult {
  ok: boolean;
  /** 设计基线死区（页脚零抖动时）：edge + feltBorderBottom。 */
  base: number;
  /** 行动态 → 稳态的页脚抖动量（稳态页脚更矮时为正，死区据此增大）。 */
  footSwing: number;
  /** 稳态死区（最坏值）= base + max(0, footSwing)。 */
  steadyDeadZone: number;
  violations: string[];
}

/**
 * AC-D6 死区不变量（BUG-04 校正口径）：**覆盖 N=2~10 全人数**。
 *
 * 英雄 pod 底沿按布局纪元冻结在「行动态页脚上沿之上 base(edge+border) 处」；稳态页脚若比行动态更矮
 * `footSwing` 像素，冻结的 pod 不跟随，死区随之增大 `footSwing`。因此：
 *   steadyDeadZone = base + max(0, footSwing) ≤ deadZoneMax。
 *
 * 旧 CI 口径盲区：只按「行动态 / 加注抽屉开启态」量测（footSwing 被掩盖为 0）、且仅 COUNTS=6，
 * 故 N=2/N=3 稳态的 32~48px 死区从未被拦。此函数把两态高度差显式纳入判定，抖动一旦复现即判红。
 */
export function assertHeroActionDeadZone(
  foot: FootLaneModel,
  policy: DeadZonePolicy = DEAD_ZONE_POLICY_DEFAULT,
): DeadZoneResult {
  const base = policy.edge + policy.feltBorderBottom;
  const footSwing = footVariableH(foot, 'acting') - footVariableH(foot, 'steady');
  const steadyDeadZone = base + Math.max(0, footSwing);
  const violations: string[] = [];
  if (steadyDeadZone > policy.deadZoneMax + policy.eps) {
    violations.push(
      `稳态死区 ${steadyDeadZone.toFixed(1)} > ${policy.deadZoneMax}（base=${base} + 页脚抖动=${footSwing.toFixed(1)}；` +
        `信息条/时限条预留未覆盖行动态自然高 → 页脚在首次行动后收矮、英雄 pod 不跟随）`,
    );
  }
  return { ok: violations.length === 0, base, footSwing, steadyDeadZone, violations };
}
